import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  FormControl,
  LinearProgress,
  Button,
  NativeSelect,
  InputBase,
  withStyles,
} from "@material-ui/core";
import moment from "moment";
import { scoreBand } from "../../utils/score-band";
import "./overall-performance-header.scss";

const OverallPerformanceHeader = (props) => {
  const { navigateToReview, performanceData, quizHistoryList,
    performanceLoading = false, performanceUnavailable = false,
    historyLoading = false, historyUnavailable = false, onRetry } = props;

  const [quizSelected, setQuizSelected] = useState("");

  const handleChange = (event) => {
    setQuizSelected(event.target.value);
  };

  const BootstrapInput = withStyles((theme) => ({
    root: {
      "label + &": {
        marginTop: theme.spacing(3),
      },
    },
    input: {
      borderRadius: 4,
      position: "relative",
      backgroundColor: theme.palette.background.paper,
      border: "1px solid #ced4da",
      fontSize: 16,
      padding: "10px 26px 10px 12px",
      transition: theme.transitions.create(["border-color", "box-shadow"]),
      "&:focus": {
        borderRadius: 4,
        borderColor: "#80bdff",
        boxShadow: "0 0 0 0.2rem rgba(0,123,255,.25)",
      },
    },
  }))(InputBase);

  const latestQuiz = quizHistoryList && quizHistoryList[0];
  const selectedQuizId = quizHistoryList?.some(quiz => quiz.id === quizSelected)
    ? quizSelected : latestQuiz?.id || "";
  const recentScore = latestQuiz?.percentageCorrect;
  const hasRecentScore = typeof recentScore === "number" && Number.isFinite(recentScore);
  const completionDate = latestQuiz?.complete ? moment(latestQuiz.complete) : null;
  const completionLabel = completionDate?.isValid()
    ? completionDate.format("lll") : "Completion date unavailable";
  let emptyPerformanceMessage = "No quizzes yet — take one to see your score.";
  if (performanceLoading) emptyPerformanceMessage = "Loading performance…";
  else if (performanceUnavailable) emptyPerformanceMessage = "Performance unavailable. Please try again.";
  else if (historyLoading) emptyPerformanceMessage = "Loading quiz history…";
  else if (historyUnavailable) emptyPerformanceMessage = "Quiz history unavailable. Please try again.";
  else if (latestQuiz) emptyPerformanceMessage = "Overall score unavailable.";

  // D2: absence ≠ zero. A brand-new user with no quizzes must not read
  // "0% CORRECT" (a demoralizing false "I failed everything" signal). For a
  // zero-quiz user the backend leaves correctPercentage undefined (that's why
  // the old `?? 0` printed a false "0%"); a genuine 0%-correct user has it as
  // 0. Gate all numeric header UI on real data presence; otherwise show a
  // neutral "—" and an inviting next step.
  const hasScoreData =
    !performanceLoading && !performanceUnavailable && (
    performanceData?.correctPercentage != null ||
    performanceData?.questionCorrect != null ||
    performanceData?.questionIncorrect != null);

  return (
    <Box component="div" className="overall-performance-header">
      <Box component="div" className="upper-header">
        <Box component="div" m={1} className="header-sidebar-left">
          <Typography component="h4" variant="h4" className="title">
            Overall Performance
          </Typography>
          <Typography component="h6" variant="h6" className="sub-title">
            Performance by Recent Test
          </Typography>
        </Box>
        <Box component="div" className="quiz-percent-cont">
          <Box component="div" className="quiz-percent-progress">
            <CircularProgress
              variant="determinate"
              className="circular-progress-bkg"
              size={80}
              thickness={2}
              value={100}
            />
            <CircularProgress
              variant="determinate"
              className={`op-score-ring op-score-ring--${scoreBand(hasScoreData ? performanceData?.correctPercentage : undefined)}`}
              size={80}
              thickness={2}
              value={hasScoreData ? (performanceData?.correctPercentage ?? 0) : 0}
            />
            <Box component="div" className="quiz-progress-text">
              {hasScoreData ? (
                <>
                  <Box component="div" className="quiz-progress-text-num">
                    {performanceData?.correctPercentage ?? 0}%
                  </Box>
                  Correct
                </>
              ) : (
                <Box component="div" className="quiz-progress-text-num" data-testid="op-score-empty">
                  —
                </Box>
              )}
            </Box>
          </Box>
          {hasScoreData ? (
            <Box component="div" className="report-details-wrapper">
              <Box component="div" className="report-details">
                <Box component="span" className="quiz-progress-text-bold">
                  {performanceData?.questionCorrect}
                </Box>
                Correct
              </Box>
              <Box component="div" className="report-details">
                <Box component="span" className="quiz-progress-text-bold">
                  {performanceData?.questionIncorrect}
                </Box>
                Incorrect
              </Box>
            </Box>
          ) : (
            <Box component="div" className="report-details-wrapper report-empty">
              <Typography variant="body2" className="op-empty-msg" role="status">
                {emptyPerformanceMessage}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {(performanceUnavailable || historyUnavailable) && onRetry && (
        <Button onClick={onRetry}>Retry</Button>
      )}
      {historyLoading && <Typography role="status">Loading recent quizzes…</Typography>}
      {!historyLoading && historyUnavailable && (
        <Typography role="status">Recent quizzes unavailable.</Typography>
      )}
      {!historyLoading && latestQuiz &&
        <Box component="div" className="lower-header">
          <Box
            component="div"
            m={1}
            className="left-sidebar"
            role="region"
            aria-label="Most recent completed quiz"
            onClick={() => navigateToReview(latestQuiz.id)}
          >
            <Box component="div" className="sidebar-header">
              <Box component="div" className="sidebar-header-left">
                <Typography component="h6" variant="h6">
                  {latestQuiz.title}
                </Typography>
                <Typography variant="overline" display="block">
                  {completionLabel}
                </Typography>
                <Typography
                  variant="overline"
                  display="block"
                  className="sub-title"
                >
                  Tutor {latestQuiz?.tutorOn ? "On" : "Off"} - Timer
                  {latestQuiz?.timerOn ? " On" : " Off"}
                </Typography>
              </Box>
              <Box component="div" m={1} className="sidebar-header-right">
                {/* D9: the whole left-sidebar is already clickable → review,
                    so the redundant "REVIEW" text link is dropped; the % ring
                    stays the sole emphasis. */}
                <Typography variant="body1">
                  {hasRecentScore
                    ? <><strong>{recentScore}%</strong> Correct</>
                    : "Score unavailable"}
                </Typography>
              </Box>
            </Box>
            {hasRecentScore && <LinearProgress
              variant="determinate"
              value={recentScore}
            />}
          </Box>
          <Box component="div" className="right-sidebar">
            <Typography component="h6" variant="h6">
              All Quizzes:
            </Typography>
            <FormControl className="dropdown">
              <NativeSelect
                id="demo-customized-select"
                value={selectedQuizId}
                onChange={handleChange}
                input={<BootstrapInput />}
              >
                {quizHistoryList?.map((item, index) => (
                  <option value={item.id} key={index}>
                    {item.title}
                  </option>
                ))}
              </NativeSelect>
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              className="review-btn"
              onClick={() => navigateToReview(selectedQuizId)}
            >
              Review
            </Button>
          </Box>
        </Box>
      }
    </Box>
  );
};

export default OverallPerformanceHeader;
